import { focusBlockEnd } from '@blocksuite/affine-shared/commands';
import {
  type BlockStdScope,
  KeymapExtension,
  TextSelection,
} from '@blocksuite/std';
import type { BlockModel } from '@blocksuite/store';

import {
  findRichTable,
  findRichTableCell,
  rowCells,
  tableRows,
} from './commands/normalize-rich-table.js';
import { insertRow } from './commands/table-ops.js';

/** Flattens the table into document order so Tab can walk it linearly. */
function cellsInOrder(table: BlockModel) {
  return tableRows(table as never).flatMap(row => rowCells(row));
}

function focusCell(std: BlockStdScope, cell: BlockModel) {
  const firstChild = cell.children[0];
  if (!firstChild) return;
  const focusBlock = std.view.getBlock(firstChild.id);
  if (!focusBlock) return;
  std.command.exec(focusBlockEnd, { focusBlock });
}

/**
 * Moves the caret out of the table, vertically.
 *
 * A table that starts or ends its note has no block beyond it for the caret to
 * move into, and Escape does not yield a block selection, so the keyboard
 * would have no way past it. Arrow keys out of the edge rows create that block
 * on demand. (A click below the page is the page's own handlers' job; they
 * skip the table's cells when looking for the page's last line.)
 */
function escapeVertically(std: BlockStdScope, delta: -1 | 1) {
  const text = std.selection.find(TextSelection);
  if (!text) return false;

  const block = std.store.getBlock(text.from.blockId);
  if (!block) return false;

  const cell = findRichTableCell(block.model);
  const table = findRichTable(cell);
  if (!cell || !table) return false;

  // Only the cell's edge block escapes; anything else moves within the cell.
  const siblings = cell.children;
  const atEdge =
    delta > 0
      ? siblings[siblings.length - 1]?.id === block.model.id
      : siblings[0]?.id === block.model.id;
  if (!atEdge) return false;

  // And only from the table's edge row.
  const rows = tableRows(table);
  const row = cell.parent;
  const rowIndex = row ? rows.findIndex(r => r.id === row.id) : -1;
  if (delta > 0 ? rowIndex !== rows.length - 1 : rowIndex !== 0) return false;

  const parent = table.parent;
  if (!parent) return false;

  const index = parent.children.indexOf(table);
  let target: BlockModel | null = parent.children[index + delta] ?? null;

  if (!target) {
    if (std.store.readonly) return false;
    const id = std.store.addBlock(
      'affine:paragraph',
      {},
      parent.id,
      delta > 0 ? index + 1 : index
    );
    target = std.store.getModelById(id);
  }
  if (!target) return false;

  const destination = target;
  std.host.updateComplete
    .then(() => {
      const focusBlock = std.view.getBlock(destination.id);
      if (focusBlock) std.command.exec(focusBlockEnd, { focusBlock });
    })
    .catch(console.error);
  return true;
}

export const RichTableKeymapExtension = KeymapExtension(std => {
  const moveBy = (delta: number) => {
    const text = std.selection.find(TextSelection);
    if (!text) return false;

    const block = std.store.getBlock(text.from.blockId);
    if (!block) return false;

    const cell = findRichTableCell(block.model);
    const table = findRichTable(cell);
    if (!cell || !table) return false;

    const cells = cellsInOrder(table);
    const index = cells.findIndex(candidate => candidate.id === cell.id);
    if (index === -1) return false;

    let target: BlockModel | undefined = cells[index + delta];

    // Tab out of the last cell grows the table rather than losing focus.
    if (!target && delta > 0) {
      insertRow(std.store, table);
      target = cellsInOrder(table)[index + delta];
    }
    if (!target) return false;

    const destination = target;
    std.host.updateComplete
      .then(() => focusCell(std, destination))
      .catch(console.error);
    return true;
  };

  return {
    Tab: ctx => {
      if (!moveBy(1)) return false;
      ctx.get('keyboardState').raw.preventDefault();
      return true;
    },
    'Shift-Tab': ctx => {
      if (!moveBy(-1)) return false;
      ctx.get('keyboardState').raw.preventDefault();
      return true;
    },
    ArrowDown: ctx => {
      if (!escapeVertically(std, 1)) return false;
      ctx.get('keyboardState').raw.preventDefault();
      return true;
    },
    ArrowUp: ctx => {
      if (!escapeVertically(std, -1)) return false;
      ctx.get('keyboardState').raw.preventDefault();
      return true;
    },
  };
});
